/** Shared rotary-knob geometry and value mapping (ported from the pointer-lock knob). */

export const TWO_PI = Math.PI * 2;
export const ROTARY_POINTER_OFFSET = Math.PI / 2;
export const LEGACY_ROTARY_START_ANGLE = (3 * Math.PI) / 4;
export const LEGACY_ROTARY_SWEEP = (3 * Math.PI) / 2;
export const POINTER_LOCK_PIXELS_PER_FULL_SWEEP = 280;
export const DOUBLE_CLICK_WINDOW_MS = 320;
export const MIN_NORMALIZED_STEP = 1e-12;
export const POINTER_LOCK_ACCEL_BASE = 0.085;
export const POINTER_LOCK_ACCEL_MAX = 18;

export type RotaryKnobInteractionMode = "directional" | "pointer-lock-vertical";
export type RotaryKnobDirection = "clockwise" | "counterclockwise";

export type RotaryKnobConfig = {
  defaultDegrees?: number | null;
  direction?: RotaryKnobDirection | null;
  rangeDegrees?: number | null;
  continuous?: boolean | null;
};

export type RotaryKnobResolvedConfig = {
  startAngle: number;
  endAngle: number;
  sweepSigned: number;
  sweepAbs: number;
  continuous: boolean;
};

export function clampValue(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function hasCustomConfig(config: RotaryKnobConfig | undefined): boolean {
  return (
    Number.isFinite(config?.defaultDegrees)
    || Number.isFinite(config?.rangeDegrees)
    || typeof config?.continuous === "boolean"
    || config?.direction === "clockwise"
    || config?.direction === "counterclockwise"
  );
}

export function resolveRotaryKnobConfig(config?: RotaryKnobConfig): RotaryKnobResolvedConfig {
  if (!hasCustomConfig(config)) {
    return {
      startAngle: LEGACY_ROTARY_START_ANGLE,
      endAngle: LEGACY_ROTARY_START_ANGLE + LEGACY_ROTARY_SWEEP,
      sweepSigned: LEGACY_ROTARY_SWEEP,
      sweepAbs: LEGACY_ROTARY_SWEEP,
      continuous: false,
    };
  }
  const defaultDegrees =
    typeof config?.defaultDegrees === "number" && Number.isFinite(config.defaultDegrees)
      ? config.defaultDegrees
      : 0;
  const continuous = Boolean(config?.continuous);
  const rangeDegrees = continuous
    ? 360
    : typeof config?.rangeDegrees === "number" && Number.isFinite(config.rangeDegrees)
    ? clampValue(config.rangeDegrees, 1, 360)
    : 270;
  const direction: RotaryKnobDirection =
    config?.direction === "counterclockwise" ? "counterclockwise" : "clockwise";
  const defaultAngle = (defaultDegrees * Math.PI) / 180;
  const sweepAbs = (rangeDegrees * Math.PI) / 180;
  const sweepSigned = direction === "counterclockwise" ? -sweepAbs : sweepAbs;
  const startAngle = defaultAngle - sweepSigned / 2;
  return {
    startAngle,
    endAngle: startAngle + sweepSigned,
    sweepSigned,
    sweepAbs,
    continuous,
  };
}

export function wrapAnglePositive(angle: number): number {
  return ((angle % TWO_PI) + TWO_PI) % TWO_PI;
}

export function clampAngleToSweep(angle: number, config: RotaryKnobResolvedConfig): number {
  if (config.continuous) {
    if (Math.abs(angle - config.startAngle) < 1e-6) {
      return config.startAngle;
    }
    if (Math.abs(angle - config.endAngle) < 1e-6) {
      return config.endAngle;
    }
    if (config.sweepSigned >= 0) {
      const delta = wrapAnglePositive(angle - config.startAngle);
      return config.startAngle + delta;
    }
    const delta = wrapAnglePositive(config.startAngle - angle);
    return config.startAngle - delta;
  }
  const minAngle = Math.min(config.startAngle, config.endAngle);
  const maxAngle = Math.max(config.startAngle, config.endAngle);
  return clampValue(angle, minAngle, maxAngle);
}

export function normalizedFromAngle(angle: number, config: RotaryKnobResolvedConfig): number {
  if (Math.abs(config.sweepSigned) < 1e-6) {
    return 0;
  }
  const clamped = clampAngleToSweep(angle, config);
  return clampValue((clamped - config.startAngle) / config.sweepSigned, 0, 1);
}

export function angleFromNormalized(normalized: number, config: RotaryKnobResolvedConfig): number {
  return config.startAngle + clampValue(normalized, 0, 1) * config.sweepSigned;
}

export function angleFromValue(
  value: number,
  min: number,
  max: number,
  config?: RotaryKnobConfig,
): number {
  const resolvedConfig = resolveRotaryKnobConfig(config);
  if (!Number.isFinite(value) || max <= min) {
    return resolvedConfig.startAngle;
  }
  const normalized = clampValue((value - min) / (max - min), 0, 1);
  return angleFromNormalized(normalized, resolvedConfig);
}

export function valueFromAngle(
  angle: number,
  min: number,
  max: number,
  step: number,
  config?: RotaryKnobConfig,
): number {
  if (max <= min) {
    return min;
  }
  const resolvedConfig = resolveRotaryKnobConfig(config);
  const normalized = clampValue(normalizedFromAngle(angle, resolvedConfig), 0, 1);
  let raw = min + normalized * (max - min);
  if (Number.isFinite(step) && step > 0) {
    const steps = Math.round((raw - min) / step);
    raw = min + steps * step;
  }
  return clampValue(raw, min, max);
}

export function angleDelta(prev: number, next: number): number {
  let delta = next - prev;
  if (delta > Math.PI) {
    delta -= TWO_PI;
  }
  if (delta < -Math.PI) {
    delta += TWO_PI;
  }
  return delta;
}

export function angleInSweep(angle: number, config: RotaryKnobResolvedConfig): boolean {
  if (config.continuous) {
    return true;
  }
  if (config.sweepSigned >= 0) {
    const delta = wrapAnglePositive(angle - config.startAngle);
    return delta <= config.sweepAbs;
  }
  const delta = wrapAnglePositive(config.startAngle - angle);
  return delta <= config.sweepAbs;
}

export function angleFromPointer(event: PointerEvent, element: HTMLElement): number {
  const rect = element.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  return Math.atan2(event.clientY - centerY, event.clientX - centerX);
}

export function pointerLockAcceleration(
  absMovement: number,
  normalizedStep: number | null,
): number {
  if (absMovement <= 1) {
    return 1;
  }
  const rangeFactor = normalizedStep ? Math.max(1, Math.log10(1 / normalizedStep)) : 1;
  const maxFactor = Math.min(POINTER_LOCK_ACCEL_MAX, 1 + rangeFactor * 3);
  const accelerated =
    1 + Math.pow(Math.max(0, absMovement - 1), 1.15) * POINTER_LOCK_ACCEL_BASE * rangeFactor;
  return clampValue(accelerated, 1, maxFactor);
}

export function supportsPointerLockApi(): boolean {
  if (typeof document === "undefined" || typeof HTMLElement === "undefined") {
    return false;
  }
  if (!("pointerLockElement" in document)) {
    return false;
  }
  return typeof HTMLElement.prototype.requestPointerLock === "function";
}
